import React, { useState, useEffect, useMemo } from 'react';
import ModalBase from './ModalBase';
import ConfirmModal from './modals/ConfirmModal';
import TextInputModal from './modals/TextInputModal';
import {
    SpecDetail,
    SpecType,
    SpecDetailString,
    SpecDetailStringList,
    SpecTableContent,
    SpecStringContent,
    SpecStringListContent,
    SpecSymbolContent,
    SpecDetailTable,
} from '../types';
import LanguageTag from './LanguageTag';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrash, faEdit } from '@fortawesome/free-solid-svg-icons';
import EllipsisOptions from './EllipsisOptions';
import '../styles/dropdowns.scss';
import '../styles/languages.scss';
import { deletePackCopySpec } from '../utils/backendServices';
import { usePackCopyContext } from '../contexts/PackCopyContext';
import SpecEditForm from './SpecEditForm';

interface SpecEditModalProps {
    specs: SpecDetail[];
    displayName: string;
    onClose: () => void;
}

const SpecEditModal: React.FC<SpecEditModalProps> = ({
    specs: initialSpecs,
    displayName,
    onClose,
}) => {
    // State for the specs data coming from SpecEditForm
    const [currentSpecs, setCurrentSpecs] = useState<SpecDetail[]>(() =>
        JSON.parse(JSON.stringify(initialSpecs)),
    );
    const [isFormModified, setIsFormModified] = useState(false);
    const [hasFormEmptyFields, setHasFormEmptyFields] = useState(false);
    const [hasFormTableError, setHasFormTableError] = useState(false);

    // Modal states for delete and name edit (these remain in the modal)
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [isEditNameModalOpen, setIsEditNameModalOpen] = useState(false);
    const [editedName, setEditedName] = useState(displayName);

    const {
        handlers: { handleSpecDelete, handleSpecUpdate },
    } = usePackCopyContext();

    // Callback for SpecEditForm to update the modal's state
    const handleSpecsChangeFromForm = (
        updatedSpecs: SpecDetail[],
        isModified: boolean,
        hasEmptyFields: boolean,
        hasTableError: boolean,
    ) => {
        // Only update currentSpecs if the stringified versions are different.
        if (JSON.stringify(currentSpecs) !== JSON.stringify(updatedSpecs)) {
            setCurrentSpecs(updatedSpecs);
        }
        // Only update boolean flags if their values have actually changed.
        if (isFormModified !== isModified) {
            setIsFormModified(isModified);
        }
        if (hasFormEmptyFields !== hasEmptyFields) {
            setHasFormEmptyFields(hasEmptyFields);
        }
        if (hasFormTableError !== hasTableError) {
            setHasFormTableError(hasTableError);
        }
    };

    const handleSave = () => {
        if (hasFormEmptyFields || hasFormTableError) {
            // Potentially show an alert or rely on button disabled state
            console.log('Save prevented due to empty fields or table errors.');
            return;
        }

        // Add/update the display name to all specs before saving
        const specsToSave = currentSpecs.map((spec) => ({
            ...spec,
            display_name: editedName,
        }));

        handleSpecUpdate(specsToSave);
        // onClose(); // Typically, context update might trigger re-render or parent closes it.
        // For now, let's assume handleSpecUpdate handles UI flow or onClose is called after success.
    };

    const deleteSpecGroup = () => {
        // This deletes the entire group of specs based on the original name (or current name if it's unique)
        // Assuming the first spec's name can identify the group for deletion if names are consistent within the group.
        if (initialSpecs.length > 0) {
            handleSpecDelete(initialSpecs[0].name);
        }
        onClose();
    };

    const handleNameEdit = (newName: string) => {
        setEditedName(newName);
        // If only the name is changed, we still want to enable save
        setIsFormModified(true);
    };

    // Determine if the save button should be disabled
    // It should be disabled if the form itself isn't modified OR if there are validation errors.
    const isSaveDisabled =
        !isFormModified || hasFormEmptyFields || hasFormTableError;

    return (
        <ModalBase onClose={onClose} width="1000px">
            <div className="spec-edit-modal-content">
                <div className="edit-header">
                    <h2>Edit {editedName}</h2>
                    <div className="top-actions">
                        <EllipsisOptions
                            size="default"
                            options={[
                                {
                                    icon: faEdit,
                                    label: 'Edit name',
                                    onClick: () => setIsEditNameModalOpen(true),
                                    variant: 'primary',
                                },
                                {
                                    icon: faTrash,
                                    label: 'Delete spec',
                                    onClick: () => setIsDeleteModalOpen(true),
                                    variant: 'danger',
                                },
                            ]}
                        />
                    </div>
                </div>

                <SpecEditForm
                    initialSpecs={initialSpecs}
                    onSpecsChange={handleSpecsChangeFromForm}
                    isCreatingNewSpec={false}
                />

                <div className="button-row">
                    <button className="btn-secondary" onClick={onClose}>
                        Cancel
                    </button>
                    <button
                        className="btn-primary"
                        onClick={handleSave}
                        disabled={isSaveDisabled}
                    >
                        Save
                    </button>
                </div>

                <ConfirmModal
                    isOpen={isDeleteModalOpen}
                    onClose={() => setIsDeleteModalOpen(false)}
                    heading={`Delete Spec: ${editedName}`}
                    text="Are you sure you want to delete this spec? This action cannot be undone."
                    confirmButtonText="Delete Spec"
                    onConfirm={deleteSpecGroup}
                />

                <TextInputModal
                    isOpen={isEditNameModalOpen}
                    onClose={() => setIsEditNameModalOpen(false)}
                    heading="Edit Spec Name"
                    defaultValue={editedName}
                    confirmButtonText="Save"
                    onConfirm={handleNameEdit}
                />
            </div>
        </ModalBase>
    );
};

export default SpecEditModal;
